import * as settings from "../../../services/settings";
import * as userService from "../../../services/users";

import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Card, Col, Form, Input, Row, Spin, Table, Typography, notification } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";

import { ModalDelete } from "../../../Components/model/modal/delete";
import { SearchOutlined } from "@ant-design/icons";
import permissions from "../../../../configs/permission";
import { useAccessToken } from "../../../contexts/AccessTokenContext";
import { useNavigate } from "react-router-dom";
import { usePermission } from "../../../hooks/usePermission";
import { useTranslation } from "react-i18next";

const initPage = { limit: 0, page: 1 };
const { Link } = Typography;
export const UserManagementPage = () => {
  const { accessToken } = useAccessToken();
  const { hasPermission, isSuperuser } = usePermission();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(initPage);
  const [usersData, setUsersData] = useState([]);
  const [form] = Form.useForm();
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();
  const { t } = useTranslation("user", "buttons", "common");
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [prepareDeleteData, setPrepareDeleteData] = useState({});

  const { Title } = Typography;

  const openNotificationWithIcon = useCallback(
    (type, message, description) => {
      api[type]({ message, description });
    },
    [api]
  );
  const fetchUsersManagement = useCallback(
    async (param) => {
      setLoading(true);
      try {
        const response = await userService.getUsers(accessToken, param);
        setSearch({ ...param });
        setTotal(response.totalCount);
        setUsersData(response.query);
      } catch (err) {
        console.error("[UserManagementPage] fetchUsersManagement: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      }
      setLoading(false);
    },
    [accessToken]
  );

  const onFinish = (value) => {
    const values = {
      username: value["search"],
    };
    search.page = 1;
    fetchUsersManagement({ ...search, values });
  };

  const items = [
    { title: t("Page_Body_Tab_Menu.Home", { ns: "menus" }) },
    { title: t("Page_Body_Tab_Menu.Menu", { ns: "menus" }) },
    {
      title: (
        <a href="/usermanagement/user" style={{ fontWeight: 600, color: "black" }}>
          {t("Title_User")}
        </a>
      ),
    },
  ];

  const columns = [
    {
      title: t("Table_User.Table_ID"),
      dataIndex: "id",
      render: (_, row) => (
        <Link
          style={{ cursor: "pointer" }}
          onClick={() => {
            navigate("/usermanagement/user/create", {
              state: { type: "EDIT", value: row },
            });
          }}
        >
          {row.id}
        </Link>
      ),
    },
    {
      title: t("Table_User.Table_Username"),
      dataIndex: "username",
    },
    {
      title: t("Table_User.Table_Email"),
      dataIndex: "email",
    },
    {
      title: t("Table_User.Table_First_Name"),
      dataIndex: "first_name",
    },
    {
      title: t("Table_User.Table_Last_Name"),
      dataIndex: "last_name",
    },
    {
      title: t("Table_User.Table_Staff_Name"),
      dataIndex: "staff_name",
    },
    {
      title: t("Table_User.Table_Manager"),
      dataIndex: "manager",
    },
    {
      title: t("Table_User.Table_Company"),
      dataIndex: "company",
      render: (_, record) => (record.app_company ? record.app_company.company_name : ""),
    },
    {
      title: t("Table_User.Table_Sales"),
      dataIndex: "sales",
      render: (_, record) => (record.app_sale ? record.app_sale.sale_name : ""),
    },
    {
      title: t("Table_User.Table_Customer"),
      dataIndex: "customer",
      render: (_, record) => (record.app_customer ? record.app_customer.customer_name : ""),
    },
    {
      title: t("Table_User.Table_Origin"),
      dataIndex: "origin",
      render: (_, record) => record.app_warehouse_app_user_origin_idToapp_warehouse?.warehouse_name,
    },
    {
      title: t("Table_User.Table_Destination"),
      dataIndex: "destination",
      render: (_, record) => record.app_warehouse_app_user_destination_idToapp_warehouse?.warehouse_name,
    },
    {
      title: t("Table_User.Table_Language"),
      dataIndex: "language",
    },
    {
      title: t("Table_User.Table_Group"),
      dataIndex: "group",
      width: "10%",
      render: (_, record) => {
        let data = [];
        if (record.app_user_groups.length > 0) {
          let group = record.app_user_groups;
          group.map((value) => {
            data.push(value.auth_group.name);
          });
        }
        return <span>{data.join(", ")}</span>;
      },
    },
    {
      title: t("Edit", { ns: "common" }),
      key: "edit",
      render: (_, record) => (
        <Button
          type="link"
          disabled={!hasPermission(permissions.User.CHANGE)}
          onClick={() => {
            navigate("/usermanagement/user/create", {
              state: { type: "EDIT", value: record },
            });
          }}
        >
          <EditOutlined />
        </Button>
      ),
    },
    {
      title: t("Delete", { ns: "common" }),
      key: "delete",
      render: (_, record) => (
        <Button
          type="link"
          disabled={!hasPermission(permissions.User.DELETE)}
          onClick={() => {
            setIsOpenDelete(true);
            setPrepareDeleteData(record);
          }}
        >
          <DeleteOutlined />
        </Button>
      ),
    },
  ];

  const onDeleteUser = async (data) => {
    let prepareDeleteData = {
      id: data.id,
    };
    setLoading(true);
    try {
      const response = await userService.deleteUser(accessToken, prepareDeleteData);
      openNotificationWithIcon(settings.NotificationType.SUCCESS, <>{"Success."}</>, <>{response}</>);
      setPrepareDeleteData({});
      setIsOpenDelete(false);
      fetchUsersManagement({ ...search });
      setLoading(false);
    } catch (err) {
      console.error("[UserManagementPage] onDeleteUser: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      setLoading(false);
    }
  };

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!accessToken || hasInitialized.current) return;
    hasInitialized.current = true;
    fetchUsersManagement(search);
  }, [accessToken, fetchUsersManagement, search]);

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row flex={1}>
          <Col span={24}>
            <Breadcrumb items={items} />
            <Row style={{ padding: "15px 0px" }}>
              <Col span={12}>
                <Title
                  level={4}
                  style={{
                    textWrap: "nowrap",
                    display: "flex",
                    gap: "7px",
                  }}
                >
                  <ArrowLeftOutlined style={{ fontSize: "20px" }} />
                  {t("Title_User")}
                </Title>
              </Col>
            </Row>
            <Form
              layout="inline"
              form={form}
              onFinish={onFinish}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  form.submit();
                }
              }}
              style={{ flexWrap: "nowrap", overflowX: "auto", minWidth: 280 }}
            >
              <Col span={21} style={{ minWidth: 200 }}>
                <Form.Item name="search">
                  <Input
                    size="default"
                    placeholder={t("Search", { ns: "common" })}
                    prefix={<SearchOutlined />}
                    style={{ minWidth: 200 }}
                  />
                </Form.Item>
              </Col>
              <Col span={3}>
                <Form.Item>
                  <Button style={{ width: "100%" }} type="primary" icon={<SearchOutlined />} htmlType="submit">
                    {t("BTN_Search", { ns: "buttons" })}
                  </Button>
                </Form.Item>
              </Col>
            </Form>
          </Col>
          <Col
            span={24}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              margin: "1rem",
              marginLeft: "-1rem",
            }}
          >
            {hasPermission(permissions.User.ADD) && (
              <Button
                // className="flex justify-end"
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  navigate("/usermanagement/user/create", {
                    state: { type: "CREATE" },
                  });
                }}
              >
                {t("Button.Add_User")}
              </Button>
            )}
            {/* <Button
              style={{ marginLeft: '1rem' }}
              // className="flex justify-end"
              type='primary'
              icon={<PlusCircleOutlined />}
              onClick={() => {
                navigate('/usermanagement/user/create', {
                  state: { type: 'EXPERORT' },
                })
              }}
            >
              {t('BTN_Export', { ns: 'buttons' })}
            </Button> */}
          </Col>
        </Row>
        <Row align="middle" justify="center">
          <Col span={24}>
            <Card>
              <Table
                loading={loading}
                scroll={{ x: "max-content" }}
                columns={columns}
                dataSource={usersData}
                rowKey={(record) => record.id}
                pagination={{
                  defaultCurrent: search.limit,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  current: search.page,
                  total: total,
                  onChange: (page, limit) => {
                    fetchUsersManagement({ ...search, page, limit });
                  },
                  showSizeChanger: true,
                }}
                style={{ cursor: "pointer" }}
              />
            </Card>
          </Col>
        </Row>
        <ModalDelete
          isOpen={isOpenDelete}
          handleCloseModal={() => {
            setIsOpenDelete(false);
          }}
          onClick={() => {
            onDeleteUser(prepareDeleteData);
          }}
          dataDetail={prepareDeleteData}
          t={t}
        />
      </Spin>
    </>
  );
};
